import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
import { Store } from '../../Store';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import { getNavLabel } from '../../utils/labelStore';
import { getUserInfo, getInitials, signoutHandler } from '../../utils';
import { getCompanyLogoSrc } from '../../utils/featureFlags';
import { STANDALONE_EDITOR_PREFIX } from '../../constants/navigation';
import './topbar.css';

/**
 * Sticky 56px header replacing AdminNavbar + SectionsNavigator: brand,
 * DB-driven section nav, a gear icon to Configuración, and a user avatar menu
 * (Mi Perfil / Salir). See docs/ui/entity-list-pattern.md and
 * .claude/plans/topbar-header-plan.md for the full spec and rationale.
 */
function Topbar({ companyName }) {
	const location = useLocation();
	const navigate = useNavigate();
	const {
		state: { tabs },
		dispatch: ctxDispatch,
	} = useContext(Store);
	const { t } = useLabels(); // re-render when terminology overrides change
	const { canSee, canEdit } = usePermissions();

	if (location.pathname.startsWith(STANDALONE_EDITOR_PREFIX)) {
		return null;
	}
	const userInfo = getUserInfo();

	// Section nav is only meaningful (and only sourced) inside /AdminScreen —
	// matches SectionsNavigator's own `!route.includes('AdminScreen')` gate.
	// On public routes (e.g. /signin) the bar still renders — brand + an
	// "Ingresar" link — same as the original AdminNavbar, so the reserved
	// height in App.css's `.sign-in` rule stays accurate.
	const inAdminScreen = location.pathname.includes('AdminScreen');
	const navItems = inAdminScreen ? tabs.filter((tab) => tab.active).sort((a, b) => a.order - b.order) : [];

	const pathParts = location.pathname.toLowerCase().split('/');
	const currentSection = pathParts[pathParts.indexOf('adminscreen') + 1];

	const handleSignout = async () => {
		ctxDispatch({ type: 'USER_SIGNOUT' });
		await signoutHandler();
	};

	return (
		<header className='app-topbar tw:flex tw:items-center tw:gap-6 tw:h-14 tw:px-6 tw:border-b tw:border-border tw:font-sans'>
			<Link
				to='/AdminScreen'
				className='tw:flex tw:items-center tw:gap-2.5 tw:no-underline tw:shrink-0'
			>
				<img src={getCompanyLogoSrc()} alt='' className='tw:max-h-8 tw:w-auto' />
				{companyName && (
					<span className='tw:hidden sm:tw:inline tw:font-semibold tw:tracking-[-0.01em] tw:text-ink-0'>
						{companyName}
					</span>
				)}
			</Link>

			<nav className='tw:ml-3 tw:flex tw:gap-0.5 tw:overflow-x-auto'>
				{navItems.map(({ schema, label }) => {
					const isActive = currentSection === schema.toLowerCase();
					return (
						<Link
							key={schema}
							to={`/AdminScreen/${schema}`}
							className={classnames(
								'app-topbar__nav-link tw:px-3 tw:py-2 tw:rounded-md tw:text-[13.5px] tw:font-medium tw:whitespace-nowrap tw:no-underline',
								isActive
									? 'app-topbar__nav-link--active tw:bg-accent-soft tw:text-accent-ink'
									: 'tw:text-ink-1 hover:tw:bg-surface-3 hover:tw:text-ink-0',
							)}
						>
							{getNavLabel(schema, label)}
						</Link>
					);
				})}
			</nav>

			<div className='tw:ml-auto tw:flex tw:items-center tw:gap-2'>
				{userInfo && canEdit() && canSee() && (
					<button
						type='button'
						onClick={() => navigate('/AdminScreen/Config')}
						title={t('settings.title')}
						aria-label={t('settings.title')}
						className='tw:w-8 tw:h-8 tw:rounded-[7px] tw:border-0 tw:bg-transparent tw:text-ink-2 tw:flex tw:items-center tw:justify-center hover:tw:bg-surface-3 hover:tw:text-ink-0'
					>
						<FaCog size={15} />
					</button>
				)}
				{userInfo ? (
					<Dropdown align='end'>
						<Dropdown.Toggle as='button' bsPrefix='app-topbar__avatar-toggle'>
							<span className='app-topbar__avatar tw:w-[30px] tw:h-[30px] tw:rounded-full tw:text-[12px] tw:font-bold tw:text-white tw:flex tw:items-center tw:justify-center'>
								{getInitials(userInfo.name)}
							</span>
						</Dropdown.Toggle>
						<Dropdown.Menu className='app-topbar__avatar-menu'>
							<Dropdown.Item className='app-topbar__avatar-menu-item' onClick={() => navigate('/profile')}>Mi Perfil</Dropdown.Item>
							<Dropdown.Divider className='app-topbar__avatar-menu-divider' />
							<Dropdown.Item className='app-topbar__avatar-menu-item app-topbar__avatar-menu-item--danger' onClick={handleSignout}>Salir</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				) : (
					<Link to='/signin' className='tw:text-[13.5px] tw:font-medium tw:text-ink-1 tw:no-underline hover:tw:text-ink-0'>
						Ingresar
					</Link>
				)}
			</div>
		</header>
	);
}

Topbar.propTypes = {
	companyName: PropTypes.string,
};

export default Topbar;
